import type { Awaitable } from '../types/Awaitable';

export type LoadingPhase = 'LOADING' | 'LOADED' | 'ERROR';

/**
 * This type declares consistent loading state including initial state
 * for components that distinguish between 'INITIAL' and 'LOADED' state.
 * It's recommended to stick with this type even in cases when initial
 * state is not needed just for consistency sake.
 */

export type LoadingState = 'INITIAL' | LoadingPhase;

export type WithLoadingIndicatorFunction<TResult> = <
  TResultInner extends TResult,
>(
  performOperation: (terminate: () => void) => Awaitable<TResultInner>,
) => Promise<TResultInner>;

/**
 * Handles automatic loading indication for asnyc data fetch
 * 1. Uses unified loading enum for consistency. You can work with simple state === 'LOADING' condition to
 *   simulate boolean variable
 * 2. Changes state to 'ERROR' and rethrows exception to prevent infinite loading
 * Can be used in command:
 * const data = await withLoadingIndication(
 *   (state) => updateState(
 *     draft => draft.loading = state
 *   ),
 *   () => apiRequest(...)
 * )
 * Or in component:
 * const [loadingState, setLoadingState] = useLoadingState();
 * const data = await withLoadingIndication(
 *   setLoadingState,
 *   () => apiRequest(...)
 * )
 */
export const withLoadingIndicator =
  <TResult>(
    setLoadingState: (state: LoadingPhase, result?: any | TResult) => void,
  ): WithLoadingIndicatorFunction<TResult> =>
  async (performOperation) => {
    setLoadingState('LOADING');
    let terminated = false;
    try {
      const result = await performOperation(() => {
        terminated = true;
      });
      if (!terminated) {
        setLoadingState('LOADED', result);
      }

      return result;
    } catch (e) {
      if (!terminated) {
        setLoadingState('ERROR', e);
      }

      throw e;
    }
  };
